import 'react-calendar/dist/Calendar.css';
import Layout from '../../components/layoutConfig/layout';
import DataTable, { TableColumn } from "react-data-table-component";
import { toast } from "react-toastify";
import { setupAPIclient } from '../../services/setupAPIclient';
import LocalStorage from "../../helpers/localstorage";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiTrash2, FiSave, FiPlusCircle, FiCheckCircle, FiSearch, FiBox, FiHash, FiEdit, FiList } from 'react-icons/fi';

interface FormRegionInput {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
}

function RegionForm() {
  const localStorage = new LocalStorage();
  const token = String(localStorage.getAuthToken());
  const [data, setData] = useState<FormRegionInput[]>([]);
  const [filterText, setFilterText] = useState("");
  const [pending, setPending] = useState(true);
  const [formRegiao, setFormRegiao] = useState({ name: "" });

  const ApiClient = setupAPIclient(token);

  // Buscar regiões
  const handleGetDataInput = async () => {
    try {
      const response = await ApiClient.post<{ success: boolean; data: FormRegionInput[] }>("/management/get/region");
      setData(response.data);
      setPending(false);
    } catch (error) {
      console.error("Erro ao buscar dados:", error);
      toast.error("Erro ao carregar dados");
    }
  };

  // Atualizar região
  const handleUpdate = async (row: FormRegionInput) => {
    try {
      await ApiClient.post("/management/update/region", { id: row.id, name: row.name });
      toast.success("Registro atualizado com sucesso");
      handleGetDataInput();
    } catch (error) {
      console.error("Erro ao atualizar:", error);
      toast.error("Erro ao atualizar registro");
    }
  };

  // Criar nova região
  const handleCreate = async () => {
    if (!formRegiao.name) {
      toast.warn("Preencha todos os campos antes de cadastrar");
      return;
    }
    try {
      await ApiClient.post("/management/create/region", { ...formRegiao });
      toast.success("Região cadastrada com sucesso");
      setFormRegiao({ name: "" });
      handleGetDataInput();
    } catch (error) {
      console.error("Erro ao cadastrar:", error);
      toast.error("Erro ao cadastrar região");
    }
  };

  // Deletar região
  const handleDelete = async (row: FormRegionInput) => {
    if (!window.confirm(`Tem certeza que deseja deletar a região ${row.name}?`)) return;
    try {
      const response = await ApiClient.post("/management/delete/region", { id: row.id });
      if (!response.data.success) {
        toast.warning(`Não é possível deletar. Existem dados associados à região ${row.name}`);
      } else {
        toast.success("Registro deletado com sucesso");
        handleGetDataInput();
      }
    } catch (error) {
      console.error("Erro ao deletar:", error);
      toast.warning(`Não é possível deletar. Existem dados associados à região ${row.name}`);
    }
  };

  useEffect(() => {
    handleGetDataInput();
  }, []);

  const iconColor = "#5dade2"; // COR DOS ÍCONES
  const buttonColor = "#e74c3c"; // COR DOS BOTÕES
  const iconSize = 20;

  const columns: TableColumn<FormRegionInput>[] = [
    {
      name: <div className="d-flex align-items-center gap-1"><FiHash color={iconColor} size={iconSize} /> ID</div>,
      selector: row => row.id,
      sortable: true,
      minWidth: "80px"
    },
    {
      name: <div className="d-flex align-items-center gap-1"><FiBox color={iconColor} size={iconSize} /> Nome</div>,
      selector: row => row.name,
      sortable: true,
      cell: (row) => (
        <input
          type="text"
          className="form-control form-control-sm"
          value={row.name ?? ""}
          onChange={(e) =>
            setData(prev => prev.map(r => r.id === row.id ? { ...r, name: e.target.value } : r))
          }
        />
      ),
      minWidth: "150px"
    },
    {
      name: <div className="d-flex align-items-center gap-1"><FiEdit color={iconColor} size={iconSize} /> Ações</div>,
      cell: (row) => (
        <div className="d-flex gap-2 justify-content-center flex-wrap">
          <motion.button 
            whileHover={{ scale: 1.1 }} 
            whileTap={{ scale: 0.95 }} 
            className="btn btn-md d-flex align-items-center gap-1"
            style={{ borderColor: buttonColor, color: buttonColor }}
            onClick={() => handleUpdate(row)}
          >
            <FiSave color={iconColor} /> 
          </motion.button>
          <motion.button 
            whileHover={{ scale: 1.1 }} 
            whileTap={{ scale: 0.95 }} 
            className="btn btn-md d-flex align-items-center gap-1"
            style={{ borderColor: buttonColor, color: buttonColor }}
            onClick={() => handleDelete(row)}
          >
            <FiTrash2 color={iconColor} /> 
          </motion.button>
        </div>
      ),
      center: true,
      minWidth: "180px"
    }
  ];

  const filteredItems = data.filter(item =>
    item.name?.toLowerCase().includes(filterText.toLowerCase())
  );

  return (
    <Layout>
      {/* Título e card de total */}
      <div className="mb-4">
        <h4 className="d-flex align-items-center gap-2">
          <FiBox color={iconColor} size={26} /> Configuração de Região
        </h4>
        <div className="d-flex gap-3 mt-3 flex-wrap">
          <div className="card flex-fill p-3 shadow-sm">
            <div className="d-flex align-items-center gap-2">
              <FiList color={iconColor} size={22} />
              <div>
                <div>Total de Regiões</div>
                <strong>{data.length}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Formulário de criação */}
      <motion.div className="card p-4 mb-4 shadow-sm" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
        <h5 className="mb-3 d-flex align-items-center gap-2">
          <FiPlusCircle color={iconColor} size={iconSize} /> Nova Região
        </h5>
        <div className="row align-items-end g-3">
          <div className="col-md-4">
            <label className="form-label">Nome da Região</label>
            <input 
              type="text" 
              className="form-control" 
              placeholder="Digite o nome da região" 
              value={formRegiao.name} 
              onChange={(e) => setFormRegiao({ ...formRegiao, name: e.target.value })} 
            />
          </div>
          <div className="col-md-2 d-grid">
            <motion.button 
              whileHover={{ scale: 1.05 }} 
              whileTap={{ scale: 0.95 }} 
              className="btn d-flex align-items-center justify-content-center gap-1"
              style={{ borderColor: buttonColor, color: buttonColor }}
              onClick={handleCreate}
            >
              <FiCheckCircle color={iconColor} /> Cadastrar
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* Pesquisa */}
      <div className="mb-3">
        <div className="input-group" style={{ maxWidth: "300px" }}>
          <span className="input-group-text bg-white"><FiSearch color={iconColor} /></span>
          <input 
            type="text" 
            className="form-control" 
            placeholder="Pesquisar região..." 
            value={filterText} 
            onChange={(e) => setFilterText(e.target.value)} 
          />
        </div>
      </div>

      {/* Tabela */}
      <DataTable
        columns={columns}
        data={filteredItems}
        progressPending={pending}
        pagination
        highlightOnHover
        pointerOnHover
        responsive
        striped
        dense
        noHeader
      />
    </Layout>
  );
}

export default RegionForm;
